/**
 * Composable for the focus of the next input field
 * Is used usually in forms to focus the next input field
 */
export function useFocusNext() {

  /**
   * Focus the next input field after a specified delay.
   *
   * @param nextFieldId The ID of the next input field
   * @param delay Delay in milliseconds before focusing the next input field
   */
  const focusNext = (nextFieldId: string, delay: number = 0): void=> {
    setTimeout(() => {
      const nextField = document.getElementById(nextFieldId);
      if (nextField) {
        nextField.focus();
      }
    }, delay);
  };

  /**
   * Find the next field to focus on within a category group
   * @param currentElement The current element
   * @param categoryGroup The current category group
   * @param idPattern Pattern to use for generating the input ID (default: 'input-')
   * @returns The ID of the next input field or null if not found
   */
  const getNextInputId = (currentElement: any, categoryGroup: any, idPattern: string = 'input-'): string | null => {
    // Get all elements from all headline groups in the category
    const allElements = categoryGroup.headlineGroups.flatMap((group: any) => group.elements);

    const currentIndex = allElements.findIndex((el: any) => el.id === currentElement.id);

    // If found and not the last element in all elements
    if (currentIndex !== -1 && currentIndex < allElements.length - 1) {
      return `${idPattern}${allElements[currentIndex + 1].id}`;
    }

    return null;
  };

  /**
   * Handle Enter key press event.
   *
   * This function manages form submission when the Enter key is pressed, implementing
   * special behavior for different form elements and conditions.
   *
   * @param event The keyboard event to handle
   * @param options Configuration options for handling the enter key
   *
   * @param options.requiredFields - Array of fields that must have a value before submission can proceed.
   * Each field needs a name (string) and a value (any). The function checks if all these fields have valid values.
   *
   * @param options.dialogOpen - Boolean indicating if a dialog is currently open.
   * If true, Enter key events will be prevented but not processed.
   *
   * @param options.specialFieldId - ID of a field that requires special handling of the Enter key.
   * Used together with specialFieldValue to prevent form submission
   * for specific field conditions (e.g. permission inputs).
   *
   * @param options.specialFieldValue - Value or function to evaluate if the specialField requires
   * special Enter key handling. Can be either a string to match exactly,
   * or a function that returns boolean based on the field's content.
   *
   * @returns true if the event was an Enter key press that was handled
   */
  const handleEnterKey = (event: KeyboardEvent, options: {
    requiredFields?: { name: string; value: any }[];
    dialogOpen?: boolean;
    specialFieldId?: string;
    specialFieldValue?: string | ((value: string) => boolean);
  }): boolean => {
    if (event.key !== 'Enter') {
      return false;
    }

    const activeElement = document.activeElement;
    const isInputField = activeElement &&
      (activeElement.tagName === 'INPUT' ||
       activeElement.tagName === 'SELECT' ||
       activeElement.classList.contains('p-inputtext') ||
       activeElement.classList.contains('p-dropdown') ||
       activeElement.classList.contains('p-multiselect'));

    // Prevent default action if a dialog is open
    if (options.dialogOpen || !isInputField) {
      return true;
    }

    // Check for special field conditions (like permission input field)
    const isSpecialField = options.specialFieldId &&
                           activeElement &&
                           activeElement.id === options.specialFieldId;

    if (isSpecialField && options.specialFieldValue) {
      // Get the value of the active element
      const elementValue = (activeElement as HTMLInputElement).value || activeElement.textContent || '';
      // Either check for a specific value or use a function to determine if the Enter key should be prevented
      if ((typeof options.specialFieldValue === 'function' && options.specialFieldValue(elementValue)) ||
        elementValue === options.specialFieldValue) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
    }

    return true;
  };

  return {
    focusNext,
    getNextInputId,
    handleEnterKey
  };
}
